import { DollarSign, PlusCircle } from "lucide-react";

import { FunctionComponent, useState } from "react";
import { useAppDispatch } from "../../../features/helpers";
import { addCreditForWallet, setAddCreditStatus } from "../../../store/WalletSlice";
import { openToast } from "../../../store/ToastSlice";
import { WalletAddCreditPayload } from "../../../models/Wallet";

import { getAssetDecimals } from "../../../utils/decimal";

import { Modal, FormField, Alert } from "../../ui";

// Mascara monetaria: o usuario digita so numeros e o valor preenche da
// direita para a esquerda ("123456" -> "1.234,56" com 2 casas). Os digitos
// ficam em string para nao perder precisao nas casas de cripto.
const MAX_DIGITS = 18;

const digitsToDecimal = (digits: string, decimals: number): string => {
    const padded = digits.padStart(decimals + 1, "0");
    const int    = padded.slice(0, padded.length - decimals).replace(/^0+(?=\d)/, "");
    const frac   = padded.slice(padded.length - decimals);
    return decimals > 0 ? `${int}.${frac}` : int;
};

const formatMasked = (digits: string, decimals: number): string => {
    const [int, frac] = digitsToDecimal(digits, decimals).split(".");
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return frac !== undefined ? `${grouped},${frac}` : grouped;
};

export interface AddValueModalProps {
    walletId: number;
    currency: string;
    /** Simbolo do asset (R$, BTC...) — define as casas decimais da mascara. */
    currencySymbol?: string;
    currencyId: number;
    organizationId: number;
    onClose: () => void;
}

const AddValueModal: FunctionComponent<AddValueModalProps> = ({ walletId, currency, currencySymbol, currencyId, organizationId, onClose }) => {

    const dispatch = useAppDispatch();

    // Casas da moeda (BRL 2, BTC 8...). Limitado a 8: mais que isso nao cabe
    // num input e nenhum credito precisa de wei.
    const decimals = Math.min(getAssetDecimals(currencySymbol ?? currency), 8);

    const [digits, setDigits]   = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError]     = useState<string>("");

    const value   = digitsToDecimal(digits, decimals);
    const display = digits ? formatMasked(digits, decimals) : "";
    const isValid = Number(value) > 0;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, "").replace(/^0+/, "");
        setDigits(raw.slice(0, MAX_DIGITS));
    };

    const handleSubmit = async () => {
        if (!isValid) return;

        setLoading(true);
        setError("");
        dispatch(setAddCreditStatus(false));

        try {
            const payload: WalletAddCreditPayload = {
                wallet_id: walletId,
                value: Number(value),
                asset_id: currencyId,
                organizationId
            };
            const ok = await dispatch(addCreditForWallet(payload));
            if (!ok) throw new Error("Erro ao adicionar valor. Tente novamente.");

            setDigits("");
            dispatch(openToast({
                title:   "Valor adicionado",
                message: "O valor foi adicionado a carteira com sucesso.",
                effect:  "success",
            }));
            onClose();
        } catch (err: any) {
            setError(err?.message || "Erro ao adicionar valor. Tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            icon={DollarSign}
            title="Adicionar Valor"
            subtitle="adicione um valor a carteira do cliente"
            footer={
                <button
                    className="btn-pill primary"
                    disabled={loading || !isValid}
                    onClick={handleSubmit}
                >
                    {loading
                        ? <div className="loader"></div>
                        : <><PlusCircle size={16} /> Adicionar</>}
                </button>
            }
        >

            {error   && <Alert variant="error">{error}</Alert>}

            <FormField label="Moeda" htmlFor="add-currency">
                <input type="text" id="add-currency" value={currency} disabled />
            </FormField>

            <FormField label="ID da Carteira" htmlFor="add-wallet-id">
                <input type="text" id="add-wallet-id" value={walletId} disabled />
            </FormField>

            <FormField label="Valor" htmlFor="add-value" suffix={currency}>
                <input
                    type="text"
                    inputMode="numeric"
                    id="add-value"
                    placeholder={formatMasked("0", decimals)}
                    value={display}
                    onChange={handleChange}
                    disabled={loading}
                />
            </FormField>
        </Modal>
    );
};

export default AddValueModal;
